import type { ApplicantCard } from '@/hooks/use-opportunity-applicants'
import type { ApplicantsBoardView } from '@/lib/list-view-options'
import { isInvitedPipelineApplicant } from '@/lib/pipeline-board-grouping'
import {
  matchesTextContainsHaystacks,
  normalizeCommaSeparatedTextQuery,
  parseCommaList,
} from '@/lib/list-filters/list-filter-params'
import {
  normalizeSkillKey,
  SKILL_FACET_DISPLAY_LIMIT,
  type TalentFacet,
} from '@/lib/talent-browse-filters'

export const EMPLOYER_JOB_APPLICANTS_SKILL_MATCH_MODE = 'and' as const

export type EmployerJobApplicantsFilters = {
  q: string
  selectedSkills: string[]
  location: string
  company: string
  stage: string
  view: ApplicantsBoardView
}

export const DEFAULT_EMPLOYER_JOB_APPLICANTS_FILTERS: EmployerJobApplicantsFilters = {
  q: '',
  selectedSkills: [],
  location: '',
  company: '',
  stage: '',
  view: 'kanban',
}

export function parseApplicantsBoardView(value: string | null): ApplicantsBoardView {
  return value === 'list' ? 'list' : 'kanban'
}

export function parseEmployerJobApplicantsFiltersFromSearchParams(
  searchParams: URLSearchParams,
): EmployerJobApplicantsFilters {
  return {
    q: normalizeCommaSeparatedTextQuery(searchParams.get('q') ?? ''),
    selectedSkills: parseCommaList(searchParams.get('skills')),
    location: normalizeCommaSeparatedTextQuery(searchParams.get('location') ?? ''),
    company: normalizeCommaSeparatedTextQuery(searchParams.get('company') ?? ''),
    stage: searchParams.get('stage') ?? '',
    view: parseApplicantsBoardView(searchParams.get('view')),
  }
}

export function applyEmployerJobApplicantsFiltersToSearchParams(
  base: URLSearchParams,
  filters: EmployerJobApplicantsFilters,
): URLSearchParams {
  const next = new URLSearchParams(base)

  if (filters.q.trim()) {
    next.set('q', normalizeCommaSeparatedTextQuery(filters.q))
  } else {
    next.delete('q')
  }

  if (filters.selectedSkills.length > 0) {
    next.set('skills', filters.selectedSkills.join(','))
  } else {
    next.delete('skills')
  }

  if (filters.location.trim()) {
    next.set('location', normalizeCommaSeparatedTextQuery(filters.location))
  } else {
    next.delete('location')
  }

  if (filters.company.trim()) {
    next.set('company', normalizeCommaSeparatedTextQuery(filters.company))
  } else {
    next.delete('company')
  }

  if (filters.stage.trim()) {
    next.set('stage', filters.stage.trim())
  } else {
    next.delete('stage')
  }

  if (filters.view !== 'kanban') {
    next.set('view', filters.view)
  } else {
    next.delete('view')
  }

  return next
}

function applicantLocationHaystacks(applicant: ApplicantCard): string[] {
  const city = applicant.profile.city?.trim() ?? ''
  const country = applicant.profile.country?.trim() ?? ''
  const haystacks = [city, country].filter(Boolean)
  if (city && country) {
    haystacks.push(`${city}, ${country}`)
  }
  return haystacks
}

function matchesApplicantKeyword(applicant: ApplicantCard, q: string): boolean {
  const haystacks = [
    applicant.profile.name,
    applicant.profile.headline ?? '',
    applicant.profile.latest_role ?? '',
    applicant.source ?? '',
  ]
  return matchesTextContainsHaystacks(haystacks, q)
}

function matchesApplicantSkills(applicant: ApplicantCard, selectedSkills: string[]): boolean {
  if (selectedSkills.length === 0) return true
  const keys = new Set((applicant.profile.top_skills ?? []).map(normalizeSkillKey))
  return selectedSkills.every((skill) => keys.has(normalizeSkillKey(skill)))
}

function matchesApplicantStage(applicant: ApplicantCard, stageFilter: string): boolean {
  const stage = stageFilter.trim()
  if (!stage) return true
  if (stage === 'invited') return isInvitedPipelineApplicant(applicant)
  if (stage === '__unassigned__') {
    return !isInvitedPipelineApplicant(applicant) && !applicant.current_stage
  }
  return applicant.current_stage?.slug === stage
}

export function filterApplicantCards(
  applicants: ApplicantCard[],
  filters: EmployerJobApplicantsFilters,
): ApplicantCard[] {
  return applicants.filter((applicant) => {
    if (!matchesApplicantKeyword(applicant, filters.q)) return false
    if (!matchesApplicantSkills(applicant, filters.selectedSkills)) return false
    if (!matchesTextContainsHaystacks(applicantLocationHaystacks(applicant), filters.location)) {
      return false
    }
    if (
      !matchesTextContainsHaystacks(
        applicant.profile.experience_companies ?? [],
        filters.company,
      )
    ) {
      return false
    }
    if (!matchesApplicantStage(applicant, filters.stage)) return false
    return true
  })
}

export function buildApplicantSkillFacets(
  applicants: ApplicantCard[],
  selectedSkills: string[] = [],
  limit = SKILL_FACET_DISPLAY_LIMIT,
): TalentFacet[] {
  const counts = new Map<string, { label: string; count: number }>()

  for (const applicant of applicants) {
    for (const skill of applicant.profile.top_skills ?? []) {
      const key = normalizeSkillKey(skill)
      if (!key) continue
      const existing = counts.get(key)
      if (existing) {
        existing.count += 1
      } else {
        counts.set(key, { label: skill.trim(), count: 1 })
      }
    }
  }

  const selectedKeys = new Set(selectedSkills.map(normalizeSkillKey))

  return Array.from(counts.entries())
    .map(([key, { label, count }]) => ({ key, label, count }))
    .sort((a, b) => {
      const aSelected = selectedKeys.has(a.key)
      const bSelected = selectedKeys.has(b.key)
      if (aSelected !== bSelected) return aSelected ? -1 : 1
      if (a.count !== b.count) return b.count - a.count
      return a.label.localeCompare(b.label)
    })
    .slice(0, limit)
}
